import { CommonNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { AlignBottomIcon } from '@/components/tiptap-editor/tiptap-icons/align-bottom-icon';
import { AlignTopIcon } from '@/components/tiptap-editor/tiptap-icons/align-top-icon';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { moveNode } from '@/hooks/use-move-node';
import { useSiblingNodeInfo } from '@/hooks/use-sibling-node-info';
import { type ReactNodeViewProps, NodeViewContent } from '@tiptap/react';
import { CopyPlusIcon, Trash2Icon } from 'lucide-react';
import { memo } from 'react';

const ShortAnswerNodeView: React.FC<ReactNodeViewProps> = ({ node, selected, editor, getPos, deleteNode, decorations }) => {
    const isEditorReadonly = useIsEditorReadonly(editor);
    const { nodeIndex, totalNodes } = useSiblingNodeInfo({
        editor,
        getPos,
        nodeTypeName: 'shortAnswer',
    });

    const handleMoveUp = () => {
        if (nodeIndex === 0) return;
        const pos = getPos();
        if (typeof pos !== 'number') return;
        moveNode(editor, 'up', { pos, node });
    };

    const handleMoveDown = () => {
        const pos = getPos();
        if (typeof pos !== 'number') return;
        moveNode(editor, 'down', { pos, node });
    };

    const handleDelete = () => {
        if (totalNodes <= 1) return;
        if (window.confirm('Are you sure you want to delete this question?')) {
            deleteNode();
        }
    };

    const handleDuplicate = () => {
        if (window.confirm('Are you sure you want to duplicate this question?')) {
            const pos = getPos();
            if (typeof pos !== 'number') return;
            editor
                .chain()
                .duplicateNode(node, pos + node.nodeSize)
                .scrollIntoView()
                .run();
        }
    };

    const isDeleteDisabled = totalNodes <= 1;

    const isMoveUpDisabled = nodeIndex === 0 || totalNodes <= 1;

    const isMoveDownDisabled = nodeIndex === totalNodes - 1 || totalNodes <= 1;

    const getMoveQuestionTooltip = (): string => {
        if (totalNodes <= 1) return 'Only one question in the question set';
        if (nodeIndex === 0) return 'This is the first question in the question set';
        if (nodeIndex === totalNodes - 1) return 'This is the last question in the question set';
        return '';
    };

    return (
        <CommonNodeViewWrapper
            decorations={decorations}
            label={`Question ${nodeIndex + 1}`}
            selected={selected}
            containerClassName="rounded-lg border-2 border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/50"
            contentClassName="pt-3 px-1"
        >
            <div className="flex gap-1">
                <div contentEditable={false}>
                    <div className="flex flex-col gap-2 ">
                        <Button
                            onClick={handleMoveUp}
                            data-style="ghost"
                            aria-label="Move question up"
                            disabled={isMoveUpDisabled || isEditorReadonly}
                            tooltip={isMoveUpDisabled ? getMoveQuestionTooltip() : 'Move up'}
                        >
                            <AlignTopIcon className="h-4 w-4" />
                        </Button>
                        <Button
                            onClick={handleMoveDown}
                            data-style="ghost"
                            aria-label="Move question down"
                            disabled={isMoveDownDisabled || isEditorReadonly}
                            tooltip={isMoveDownDisabled ? getMoveQuestionTooltip() : 'Move down'}
                        >
                            <AlignBottomIcon className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
                <div className="w-full">
                    <NodeViewContent />
                </div>
                <div className="flex flex-col">
                    <Button
                        onClick={handleDelete}
                        data-style="ghost"
                        aria-label="Delete question"
                        disabled={isDeleteDisabled || isEditorReadonly}
                        tooltip={isDeleteDisabled ? 'A question set must have at least one question' : 'Delete question'}
                    >
                        <Trash2Icon className="h-4 w-4" />
                    </Button>
                    <Button
                        onClick={handleDuplicate}
                        data-style="ghost"
                        aria-label="Duplicate question"
                        disabled={isEditorReadonly}
                        tooltip="Duplicate question"
                    >
                        <CopyPlusIcon className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </CommonNodeViewWrapper>
    );
};

export default memo(ShortAnswerNodeView);
